// imp this is to render as a page

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import useAppTheme from 'hooks/useAppTheme';
import { v4 as uuidv4 } from 'uuid';
import { type ConversationAPIPayloadType } from './types/chatbot.request.types';
import ChatbotHandler from 'handlers/chatbot.handler';
import { WelcomeScreen } from './components/WelcomeScreen';
import { ChatLog } from './components/ChatLog';
import { SessionHistory } from './components/SessionHistory';
import { SuggestionsCarousel } from './components/SuggestionsCarousel';
import { ChatInput } from './components/ChatInput';
import { useChatbotContext } from './context/ChatbotContext';
import {
  type ConversationAPIResponseType,
  type InspectionImage,
} from './types/chatbot.response.types';
import { redirectionMap } from './constants/constants';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export interface SessionMessageType {
  messageId: string;
  role: 'user' | 'assistant';
  loading?: boolean; // to track individual msg loading
  content: {
    type: string;
    data: string;
    navigationPath?: string;
    followUpSuggestions?: string[];
    image?: string;
    inspectionImages?: InspectionImage[];
    report?: {
      type: 'monthly_tire_performance' | 'tire_inflation';
      startDate?: string;
      endDate?: string;
    };
  };
}

export interface SessionType {
  sessionId: string;
  messages: SessionMessageType[];
  loading?: boolean; // to track if any of msg is laoding
}

const predefinedQuestions = [
  { name: 'Yesterday production?', navigate: '/production/overview' },
  { name: 'Yesterday utilization?', navigate: '/production/overview' },
  { name: 'Highest fuel cost truck', navigate: '/fuel-usage/overview' },
  { name: 'Truck with most DTC alerts', navigate: '/diagnostic-trouble-codes' },
];

export const Chatbot = () => {
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { isExpanded } = useChatbotContext();
  const chatbotHandler = new ChatbotHandler();

  const [sessionData, setSessionData] = useState<SessionType | null>(null);
  const [chatStarted, setChatStarted] = useState(false);
  const [showPersistentSuggestions, setShowPersistentSuggestions] =
    useState(false);
  const [latestFollowUps, setLatestFollowUps] = useState<string[]>([]);
  const [showSessionMap, setShowSessionMap] = useState(false);
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null);
  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const scrollToMessage = (messageId: string) => {
    const el = messageRefs.current[messageId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveMessageId(messageId);
    }
  };

  const askQuestion = async (question: string) => {
    if (sessionData?.loading) return; // api call already in progress return early
    if (!chatStarted) setChatStarted(true);

    const sessionId = sessionData?.sessionId ?? uuidv4();

    // user asked questn
    const userMessage: SessionMessageType = {
      messageId: uuidv4(),
      role: 'user',
      content: { type: 'text', data: question },
    };

    // loading msg
    const loadingAIMessage: SessionMessageType = {
      messageId: uuidv4(),
      role: 'assistant',
      loading: true,
      content: { type: 'text', data: 'Loading...' },
    };

    // updating user and loading msg
    setSessionData((prev) => ({
      sessionId,
      messages: prev
        ? [...prev.messages, userMessage, loadingAIMessage]
        : [userMessage, loadingAIMessage],
      loading: true,
    }));

    // api payload
    const payload: ConversationAPIPayloadType = {
      sessionId,
      message: question,
      context: {},
    };

    try {
      const apiResponse: ConversationAPIResponseType =
        await chatbotHandler.conversation(payload);
      const navigationPath =
        redirectionMap?.[apiResponse?.intentId as keyof typeof redirectionMap]
          ?.navigationPath || '';
      const followUps = apiResponse.follow_up_suggestions ?? [];
      if (followUps.length > 0) {
        setLatestFollowUps(followUps);
        setShowPersistentSuggestions(true);
      } else {
        setLatestFollowUps([]);
      }
      //  replacing loading and new msg
      setSessionData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: prev.messages.map((msg) =>
            msg.messageId === loadingAIMessage.messageId
              ? {
                  ...msg,
                  loading: false,
                  content: {
                    ...msg.content,
                    data: apiResponse.response,
                    navigationPath,
                    followUpSuggestions:
                      apiResponse.follow_up_suggestions ?? [],
                    image: apiResponse.image ?? undefined,
                    inspectionImages:
                      apiResponse.inspection_images ?? undefined,
                    report: apiResponse.report ?? undefined,
                  },
                }
              : msg
          ),
          loading: false,
        };
      });
    } catch (err) {
      console.error('Chatbot API error', err);

      // replacing loading with fallback message
      setSessionData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: prev.messages.map((msg) =>
            msg.messageId === loadingAIMessage.messageId
              ? {
                  ...msg,
                  loading: false,
                  content: {
                    ...msg.content,
                    data: 'Sorry, something went wrong. Please try again.',
                  },
                }
              : msg
          ),
          loading: false,
        };
      });
    }
  };

  const handleNewChat = () => {
    setSessionData({
      sessionId: uuidv4(),
      messages: [
        {
          messageId: uuidv4(),
          role: 'assistant',
          content: { type: 'text', data: 'Hi! How may I help you today?' },
        },
      ],
    });
    setActiveMessageId(null);
    setChatStarted(true);
    setShowSessionMap(false);
    setLatestFollowUps([]);
    setShowPersistentSuggestions(false);
  };

  useLayoutEffect(() => {
    if (!sessionData?.messages.length) return;
    const lastMsg = sessionData.messages[sessionData.messages.length - 1];
    const lastEl = messageRefs.current[lastMsg.messageId];
    if (lastEl) {
      requestAnimationFrame(() => {
        lastEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        setActiveMessageId(lastMsg.messageId);
      });
    }
  }, [sessionData?.messages]);

  // to look up for expad changes and close session history if it is open
  useEffect(() => {
    if (!isExpanded) {
      if (showSessionMap) {
        setShowSessionMap(false);
      }
    }
  }, [isExpanded]);

  const predefinedQuestionsName = useMemo(() => {
    const names = predefinedQuestions.map((el) => el.name);
    return [...names].sort(() => Math.random() - 0.5);
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          gap: 2,
        }}
      >
        <Box
          sx={{
            flex: showSessionMap ? 0.7 : 1,
            overflow: 'auto',
            width: '70%',
            transition: 'flex 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            px: isExpanded ? 2 : 1,
            py: 0.5,
          }}
        >
          {!chatStarted ? (
            <WelcomeScreen
              onSelect={askQuestion}
              predefinedQuestions={predefinedQuestionsName}
              showPersistentSuggestions={showPersistentSuggestions}
            />
          ) : (
            <ChatLog
              session={sessionData}
              messageRefs={messageRefs}
              activeMessageId={activeMessageId}
            />
          )}
        </Box>

        {isExpanded && chatStarted && !showSessionMap && (
          <IconButton
            onClick={() => {
              setShowSessionMap((prev) => !prev);
            }}
            size="small"
            sx={{
              position: 'absolute',
              top: '50%',
              right: showSessionMap ? 0 : -5,
              transform: 'translateY(-50%)',
              backgroundColor: enableDarkTheme ? '#152642' : '#E3E8F0',
              color: enableDarkTheme ? '#FFA403' : palette.primary,
              borderRadius: '50%',
              zIndex: 100,
              '&:hover': {
                backgroundColor: enableDarkTheme ? '#1D2E4A' : '#D0D7E2',
              },
              transition: 'right 0.3s ease',
              p: 0,
            }}
          >
            {showSessionMap ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        )}

        {showSessionMap && (
          <SessionHistory
            session={sessionData}
            activeMessageId={activeMessageId}
            onScrollTo={scrollToMessage}
            onClose={() => {
              setShowSessionMap(false);
            }}
          />
        )}
      </Box>

      <Box
        width={'100%'}
        sx={{
          background: enableDarkTheme
            ? ' rgba(26, 32, 44, 0.5)'
            : 'rgba(148, 163, 184, 0.7)',
          borderRadius: '0px 0px 12px 12px',
          backdropFilter: 'blur(8px)',
          borderTop: enableDarkTheme
            ? '1px solid rgba(100, 149, 237, 0.3)'
            : '1px solid  rgba(37, 99, 235, 0.2)',
        }}
      >
        {chatStarted && showPersistentSuggestions && !sessionData?.loading && (
          <Box sx={{ position: 'relative' }}>
            <SuggestionsCarousel
              suggestions={
                latestFollowUps.length > 0
                  ? latestFollowUps
                  : predefinedQuestionsName
              }
              onSelect={askQuestion}
            />
          </Box>
        )}
        <ChatInput
          onSubmit={askQuestion}
          onToggleSuggestions={() => {
            setShowPersistentSuggestions((prev) => !prev);
          }}
          handleNewChat={handleNewChat}
          chatStarted={chatStarted}
          loading={sessionData?.loading ?? false}
          showPersistentSuggestions={showPersistentSuggestions}
        />
      </Box>
    </Box>
  );
};
